import { useState, KeyboardEvent, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBalance } from '@/hooks/useBalance';
import { InsufficientBalanceModal } from './InsufficientBalanceModal';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  initialMessage?: string;
  isAnonymous?: boolean;
  showDisclaimer?: boolean;
}

export const ChatInput = ({ onSendMessage, disabled = false, initialMessage, isAnonymous = false, showDisclaimer = true }: ChatInputProps) => {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { balance, isZeroBalance } = useBalance();
  const navigate = useNavigate();

  // Detect mobile screen size (< 768px = md breakpoint)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (initialMessage) {
      setInput(initialMessage);
    }
  }, [initialMessage]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';

      // Calculate max height: smaller of 25vh or 300px
      const viewportHeight = window.innerHeight;
      const maxHeight = Math.min(viewportHeight * 0.25, 300);

      // Set height to scrollHeight, but not exceeding maxHeight
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;

      // On mobile with no input, keep it compact
      if (isMobile && !input.trim()) {
        textarea.style.height = 'auto';
      }
    }
  }, [input, isMobile]);

  const handleSubmit = () => {
    if (input.trim() && !disabled) {
      // Check if authenticated user has zero balance
      if (!isAnonymous && isZeroBalance) {
        setShowInsufficientBalanceModal(true);
        return;
      }

      onSendMessage(input);
      setInput('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Disable sending if authenticated user has zero balance
  const isInputDisabled = disabled || (!isAnonymous && isZeroBalance);
  const isSendDisabled = isInputDisabled || !input.trim();
  const placeholder = !isAnonymous && isZeroBalance
    ? "Add credits to continue..."
    : isMobile
    ? "Ask about HTL..."
    : "Ask me something about the Hospitality, Tourism and Leisure industry...";

  return (
    <>
      <div
        className="border-t border-neutral-light-gray dark:border-neutral-dark-gray bg-neutral-white dark:bg-neutral-black px-6 py-6"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-end space-x-3">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={isInputDisabled}
              rows={1}
              className={`flex-1 resize-none rounded-xl border border-neutral-medium-gray/30 dark:border-neutral-dark-gray bg-neutral-white dark:bg-neutral-black px-5 text-body text-neutral-black dark:text-neutral-white placeholder-neutral-medium-gray/60 dark:placeholder-neutral-medium-gray/60 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed shadow-sm overflow-y-auto ${isMobile ? 'py-2.5 leading-tight' : 'py-3.5'}`}
              style={isMobile ? {} : { minHeight: '56px' }}
            />
            <button
              onClick={handleSubmit}
              disabled={isSendDisabled}
              className={`bg-brand-teal hover:bg-brand-teal-hover text-neutral-white rounded-xl font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-brand-teal shadow-sm hover:shadow-md ${isMobile ? 'px-4 py-3.5' : 'px-7 py-3.5'}`}
            >
              {isMobile ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9-7-9-7M5 12h14" />
                </svg>
              ) : (
                'Send'
              )}
            </button>
          </div>
          {!isMobile && showDisclaimer && (
            <p className="text-caption text-neutral-medium-gray dark:text-neutral-medium-gray mt-3 text-center">
              ChatHTL may produce inaccurate information. Verify important details.
            </p>
          )}
        </div>
      </div>

      <InsufficientBalanceModal
        isOpen={showInsufficientBalanceModal}
        onClose={() => setShowInsufficientBalanceModal(false)}
        balance={balance}
        onAddCredits={() => navigate('/manage-plan')}
      />
    </>
  );
};
